// SCR-10 my records · consent · settings (plan 8.4.10, 11.3): record level (company cap ∩ my choice), pause/resume,
// withdraw (auto full deletion request), re-consent, deletion requests, my copy, protection summary, who viewed my
// records, my viewing activity, profile, login sessions, preferences. Everything here is the caller's own data.
import { z } from 'zod';
import { AuthProvider, CONSENT_ITEMS, ConsentItem, ConsentState, DeletionScope, DeletionStatus, ReasonCategory, RecordLevel, RedactionKind } from '../enums.js';
import { Count, EpochMs, ExportFormat, Id, LocalizedText, PersonRef, RecordRef, RoleRef, TeamRef, Ymd } from '../common.js';
import { DisplayPrefs, HeldRole } from './auth.js';

const ItemMap = z.record(ConsentItem, z.boolean());

/** GET /api/v1/me/consent — current consent; `effectiveItems` = company cap ∩ my choice. */
export const ConsentView = z.object({
  state: ConsentState,
  /** Pause end (state paused). */
  until: EpochMs.nullable(),
  level: RecordLevel.nullable(),
  /** My choice; `server_upload` is null for people who do not use the PC helper. */
  items: z.record(ConsentItem, z.boolean().nullable()),
  effectiveItems: ItemMap,
  custom: z.boolean(),
  cap: z.object({ items: ItemMap, maxLevel: RecordLevel }),
  agreedNoticeVersion: z.string().nullable(),
  currentNotice: z.object({ version: z.string(), publishedAt: EpochMs, hash: z.string(), changes: LocalizedText.nullable() }),
  reconsentRequired: z.boolean(),
  grantedAt: EpochMs.nullable(),
  updatedAt: EpochMs.nullable(),
  withdrawnAt: EpochMs.nullable(),
  /** Last stored record and number of PCs ("기록 중 — 수준 · 마지막 저장 · 연결 PC 수"). */
  lastSavedAt: EpochMs.nullable(),
  pcCount: Count,
  /** The level changed since the CLI settings were issued → "CLI 설정을 다시 받아야 해요". */
  resyncNeeded: z.boolean(),
  /** Withdrawal can be undone until this instant (10 s snackbar). */
  undoWithdrawUntil: EpochMs.nullable(),
});
export type ConsentView = z.infer<typeof ConsentView>;

/**
 * PUT /api/v1/me/consent — choose a level (cumulative ①–④) or custom items ('직접 고르기'). Above the cap → 422; basic is
 * always on. Turning work details on — level ④ or `items.work_detail: true` — needs `secretRiskAck` ("명령·도구 입력에
 * 비밀값이 들어갈 수 있어요", plan 11.3.1 ④); turning tool_output on needs `toolOutputRiskAck` (11.3.1 ⑤). Missing ack →
 * 422 `risk_ack_required` with `details.ack` = the missing flag name.
 */
export const ConsentChangeRequest = z.union([
  z.object({ level: RecordLevel, secretRiskAck: z.boolean().optional(), source: z.enum(['settings', 'connect_wizard']).default('settings') }).strict(),
  z
    .object({
      items: z.partialRecord(ConsentItem, z.boolean()),
      secretRiskAck: z.boolean().optional(),
      toolOutputRiskAck: z.boolean().optional(),
      source: z.enum(['settings', 'connect_wizard']).default('settings'),
    })
    .strict(),
]);
export type ConsentChangeRequest = z.input<typeof ConsentChangeRequest>;
/** POST /api/v1/me/consent/agree — first consent or re-consent; the shown notice must match (409 notice_changed). */
export const ConsentAgreeRequest = z.object({ noticeVersion: z.string(), noticeHash: z.string() });
export const ConsentPauseRequest = z
  .object({ preset: z.enum(['1h', 'today', 'until']), until: EpochMs.optional() })
  .superRefine((b, ctx) => {
    if (b.preset === 'until' && b.until == null) ctx.addIssue({ code: 'custom', message: 'until required', path: ['until'] });
  });
export const WithdrawRequest = z.object({ confirm: z.literal(true) });

/** User-facing deletion request (plan 11.9.2): blocked from every view immediately; processed in ≤ 7 days. */
export const MyDeletionRequestRow = z.object({
  id: Id,
  /** Display code ("DR-2088"). */
  code: z.string().regex(/^DR-\d{4,}$/),
  scope: DeletionScope,
  record: RecordRef.nullable(),
  from: Ymd.nullable(),
  to: Ymd.nullable(),
  count: Count,
  /** Created automatically by a consent withdrawal. */
  auto: z.boolean(),
  status: DeletionStatus,
  requestedAt: EpochMs,
  dueAt: EpochMs.nullable(),
  handledAt: EpochMs.nullable(),
  /** Processor's note shown to me (hold / reject). */
  memo: z.string().nullable(),
});
export const WithdrawResponse = z.object({ consent: ConsentView, deletionRequest: MyDeletionRequestRow, undoUntil: EpochMs });

export const CONSENT_HISTORY_ACTIONS = ['grant', 'change', 'reconsent', 'pause', 'resume', 'custom', 'withdraw', 'withdraw_undo'] as const;
/** Append-only consent history (plan 11.3.1). */
export const ConsentHistoryResponse = z.object({
  items: z.array(
    z.object({
      at: EpochMs,
      action: z.enum(CONSENT_HISTORY_ACTIONS),
      level: RecordLevel.nullable(),
      notice: z.string().nullable(),
      until: EpochMs.nullable(),
      items: z.array(ConsentItem).nullable(),
      source: z.enum(['settings', 'connect_wizard', 'login_gate', 'system']),
    }),
  ),
});

/** The published notice snapshot (10 sections, ko/en). Auto sections are frozen at publish time. */
export const NoticeView = z.object({
  version: z.string(),
  publishedAt: EpochMs,
  hash: z.string(),
  changes: LocalizedText.nullable(),
  sections: z.array(z.object({ n: z.int().min(1).max(10), body: LocalizedText })).length(10),
});
export type NoticeView = z.infer<typeof NoticeView>;

/** "내 기록 보호 현황" (plan 8.4.10, 11.3.6): what is kept, retention, who may view, redaction counts (7/30 days). */
export const ProtectionResponse = z.object({
  kept: z.array(ConsentItem),
  notKept: z.array(ConsentItem),
  retention: z.object({ contentDays: Count, usageDays: Count, backupDays: Count }),
  viewers: z.object({ flow: z.array(RoleRef), team: z.array(RoleRef), meta: z.array(RoleRef) }),
  redactions: z.array(z.object({ kind: RedactionKind, d7: Count, d30: Count })),
  secretPathAccess: z.object({ d7: Count, d30: Count }),
  sessions30: Count,
});

export const VIEW_EVENT_KINDS = ['content_view', 'person_view', 'export', 'deletion', 'legal_hold'] as const;
/** "누가 내 기록을 봤나요" — role only, never the viewer's name (DC-06 default). */
export const MyViewsQuery = z.object({ kind: z.enum(VIEW_EVENT_KINDS).optional(), cursor: z.string().max(200).optional(), limit: z.coerce.number().int().min(1).max(100).default(30) });
export const MyViewsResponse = z.object({
  /** `title` = my own conversation's first-question summary (I am the owner), null when no question text is kept. */
  items: z.array(
    z.object({
      at: EpochMs,
      kind: z.enum(VIEW_EVENT_KINDS),
      result: z.enum(['success', 'denied']),
      role: RoleRef.nullable(),
      reasonCategory: ReasonCategory.nullable(),
      reason: z.string().nullable(),
      record: RecordRef.nullable(),
      title: z.string().nullable(),
      /** Investigation display code only, released after any approved notice deferral. */
      caseCode: z.string().nullable(),
    }),
  ),
  counts: z.object({ byKind: z.partialRecord(z.enum(VIEW_EVENT_KINDS), Count), byRole: z.array(z.object({ role: RoleRef, count: Count })), total: Count, latestAt: EpochMs.nullable() }),
  nextCursor: z.string().nullable(),
});

/** "내 열람 활동" — only for holders of text/individual-view permissions beyond self. */
export const MyAccessActivityResponse = z.object({
  grants: z.array(z.object({ at: EpochMs, record: RecordRef, owner: PersonRef, reasonCategory: ReasonCategory.nullable(), reason: z.string().nullable(), expiresAt: EpochMs, active: z.boolean(), minutesLeft: z.int().nonnegative() })),
  personViews: z.array(z.object({ at: EpochMs, person: PersonRef, reasonCategory: ReasonCategory.nullable(), reason: z.string().nullable() })),
  denied: z.array(z.object({ at: EpochMs, record: RecordRef.nullable(), person: PersonRef.nullable(), reasonCategory: ReasonCategory.nullable(), reason: z.string().nullable() })),
  counts: z.object({ views: Count, personViews: Count, denied: Count, openGrants: Count }),
});

export const MyDeletionListResponse = z.object({ items: z.array(MyDeletionRequestRow), pending: Count });
export const DeletionCreateRequest = z
  .object({ scope: DeletionScope, recordId: Id.optional(), from: Ymd.optional(), to: Ymd.optional() })
  .superRefine((b, ctx) => {
    if (b.scope === 'session' && !b.recordId) ctx.addIssue({ code: 'custom', message: 'recordId required', path: ['recordId'] });
    if (b.scope === 'range' && (!b.from || !b.to || b.from > b.to)) ctx.addIssue({ code: 'custom', message: 'from ≤ to required', path: ['from'] });
  });
export const DeletionParams = z.object({ requestId: Id });

/** POST /api/v1/me/export — my own copy (text included by default); no reason, audited (plan 11.10 본인 사본). */
export const MyCopyRequest = z.object({ period: z.enum(['30d', '90d', 'all']), format: ExportFormat, withText: z.boolean().default(true) });

export const LoginSessionRow = z.object({
  id: Id,
  current: z.boolean(),
  browser: z.string().nullable(),
  os: z.string().nullable(),
  method: AuthProvider,
  createdAt: EpochMs,
  lastUsedAt: EpochMs,
  /** Partially masked IP. */
  ip: z.string().nullable(),
});
export const LoginSessionListResponse = z.object({ items: z.array(LoginSessionRow) });
export const SessionParams = z.object({ sessionId: Id });
export const RevokeSessionsResponse = z.object({ ended: Count });

export const NotifyPrefs = z.object({ deviceDown: z.boolean(), recordViewed: z.boolean(), tokenExpiring: z.boolean(), channel: z.enum(['app', 'both']) });
export type NotifyPrefs = z.infer<typeof NotifyPrefs>;
/** Display + notification preferences (re-consent alerts cannot be turned off). */
export const PreferencesResponse = z.object({ display: DisplayPrefs, notify: NotifyPrefs, availableChannels: z.array(z.enum(['app', 'both'])).optional() });
export const PreferencesUpdateRequest = z.object({ display: DisplayPrefs.partial().strict().optional(), notify: NotifyPrefs.partial().strict().optional() }).strict();

export const ProfileResponse = z.object({
  displayName: z.string(),
  email: z.string().nullable(),
  loginId: z.string().nullable(),
  userCode: z.string(),
  teams: z.array(z.object({ team: TeamRef, path: z.array(z.string()) })),
  roles: z.array(HeldRole),
  loginMethod: AuthProvider,
  mfa: z.enum(['enrolled', 'not_enrolled', 'not_required', 'google']),
  pledge: z.object({ version: z.string(), ackedAt: EpochMs }).nullable(),
  sessionPolicy: z.object({ idleMinutes: z.int().positive(), absoluteHours: z.int().positive(), admin: z.boolean() }),
});

export const meSchemas = {
  ConsentView,
  ConsentChangeRequest,
  ConsentAgreeRequest,
  ConsentPauseRequest,
  WithdrawRequest,
  WithdrawResponse,
  ConsentHistoryResponse,
  NoticeView,
  ProtectionResponse,
  MyViewsQuery,
  MyViewsResponse,
  MyAccessActivityResponse,
  MyDeletionRequestRow,
  MyDeletionListResponse,
  DeletionCreateRequest,
  DeletionParams,
  MyCopyRequest,
  LoginSessionListResponse,
  SessionParams,
  RevokeSessionsResponse,
  PreferencesResponse,
  PreferencesUpdateRequest,
  ProfileResponse,
} as const;
export const ALL_CONSENT_ITEMS = CONSENT_ITEMS;
